import { ReactNode } from 'react'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import InboxIcon from '@mui/icons-material/Inbox'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import Button from '@mui/material/Button'

/** Empty-state card for tables and lists that returned zero rows. */
export function EmptyState({
  title = 'Nothing here yet',
  description,
  icon,
  action,
}: {
  title?: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
}) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 6, color: 'text.secondary' }}>
      <Box sx={{ fontSize: 48, color: 'action.disabled' }}>
        {icon ?? <InboxIcon fontSize="inherit" />}
      </Box>
      <Typography variant="subtitle1" color="text.primary">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" textAlign="center" sx={{ maxWidth: 480 }}>
          {description}
        </Typography>
      )}
      {action && <Box pt={1}>{action}</Box>}
    </Stack>
  )
}

/** Error card for fetch / mutation failures. */
export function ErrorCard({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string
  message?: string
  onRetry?: () => void
}) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
      <Box sx={{ fontSize: 48, color: 'error.main' }}>
        <ErrorOutlineIcon fontSize="inherit" />
      </Box>
      <Typography variant="subtitle1" color="error.main">
        {title}
      </Typography>
      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 520 }}
        >
          {message}
        </Typography>
      )}
      {onRetry && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </Stack>
  )
}

/** Table-shaped skeleton — N rows × M columns of muted blocks. */
export function TableSkeleton({
  rows = 6,
  columns = 5,
  rowHeight = 36,
}: {
  rows?: number
  columns?: number
  rowHeight?: number
}) {
  return (
    <Box sx={{ p: 1 }}>
      {Array.from({ length: rows }).map((_, r) => (
        <Stack
          key={r}
          direction="row"
          spacing={2}
          sx={{ py: 0.75, alignItems: 'center' }}
        >
          {Array.from({ length: columns }).map((__, c) => (
            <Skeleton
              key={c}
              variant="rectangular"
              height={rowHeight - 12}
              sx={{
                flex: c === 0 ? 0.4 : 1,
                borderRadius: 1,
              }}
            />
          ))}
        </Stack>
      ))}
    </Box>
  )
}
